import { createCanvas, error, getImage, warn } from "../../utils/utils";
import canvg from 'canvg';

/**
 * @class SugarRendererV0
 */
export default class SugarRendererV0 {
    surface = null;
    svg = "";
    canvas = null;
    env = "prod";
    surfaceData = { width: null, height: null, origWidth: null, origHeight: null };
    defaultFoilColor = null;
    isUmp = false;
    image = null;

    /**
     * Constructing a new instance of SugarRendererV0
     * @memberOf SugarRendererV0
     * @param {String} env
     * @param {HTMLCanvasElement|String} surface - can be canvas or base64 image
     * @param {String} svg
     * @example
     * //the example's base 64 image is 1x1 for saving space
     * const renderer = new SurfaceRenderer( "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7","<svg></svg>" )
     */
    constructor(env, surface, svg, defaultFoilColor, childSkus, isLandscape, isUmp, image) {
        this.surface = surface;
        this.svg = svg;
        this.env = env;
        this.defaultFoilColor = defaultFoilColor;
        this.childSkus = childSkus;
        this.isLandscape = isLandscape;
        this.isUmp = isUmp;
        this.image = image;

        this.validate();
        this.beforeRender();
    }

    /**
     * An output method which returns the generated product
     * @private
     * @memberOf SugarRendererV0
     * @param {Boolean} returnImage - if to return as image (default is false, return as canvas)
     * @return HTMLCanvasElement|String
     */
    async output(returnImage = false) {
        const canvas = await this.constructor.svgToPng(this.surfaceData.width, this.surfaceData.height, this.svg);
        return returnImage ? canvas.toDataURL() : canvas;
    }

    /**
     * Process the svg and removing unused stuff from it
     * @private
     * @memberOf SugarRendererV0
     */
    processSvg() {
        try {

            this.svg = this.svg.replace('display="none"', '');
            if (this.defaultFoilColor) {
                this.svg = this.svg.replace('fill="none"', `fill="${this.defaultFoilColor}"`);
            }

        } catch (e) {
            warn("Warning", `failed to clean svg - ${e.message}`);
        }
    }

    /**
     * A pre-flight check before rendering for converting the surface input into base64 image for svg rendering
     * @private
     * @memberOf SugarRendererV0
     */
    beforeRender() {
        if (typeof this.surface === "object" && this.surface.constructor.name === "HTMLCanvasElement") {
            this.surfaceData = {
                width: this.surface.width,
                height: this.surface.height,
                origWidth: this.surface.dataset.origWidth,
                origHeight: this.surface.dataset.origHeight
            };

            this.surface = this.surface.toDataURL();
        }
    }

    /**
     * Validates the initial input (surface and svg)
     * @private
     * @memberOf SugarRendererV0
     */
    validate() {
        if (this.surface === null || this.surface === "" || (typeof this.surface === "object" && this.surface.constructor.name !== "HTMLCanvasElement")) {
            error("Validation Error", "input surface must be a canvas or base64 image");
        }

        if (this.svg === null || this.svg === "") {
            error("Validation Error", "input svg must be a string");
        }
    }

    /**
     * Draws the surface over the svg inside of canvas area
     * @private
     * @memberOf SugarRendererV0
     */
    drawSurface() {
        this.svg = this.svg.replace("%SURFACE%", this.surface);
    }

    /**
     * Renders the Sugar Product
     * @public
     * @memberOf SugarRendererV0
     * @param {Boolean} returnImage - if to return as an image (default is false, as a canvas)
     * @return HTMLCanvasElement|String
     * @example
     * renderer.render().then(canvas=>{
     *     console.log(canvas)
     * });
     */
    async render(returnImage = false) {
        try {
            //clean unused stuff
            this.processSvg();

            //draw surface over svg
            await this.drawSurface();

            //replace all other assets with base64 assets (downloads and converts in parallel)
            try {

                await this.downloadSVGAssets();
                return await this.output(returnImage);

            } catch (e) {
                error("Error", "failed to render");
            }


        } catch (e) {
            error("Error", e);
        }
    }

    /**
     * Downloads all assets from the svg (first matching urls and then replacing with base64 version)
     * @private
     * @memberOf SugarRendererV0
     */
    async downloadSVGAssets() {
        const urls = (this.svg.match(/http(.*?)"|\/\/(.*?)"/gm) || []).map(
            url => url.replace(/"/, '')).filter(
                _ => _.indexOf("www.w3") === -1 && _.indexOf(".") !== -1); //image link, not xmlns and is link (dots)

        if (urls.length > 0) {
            const images = await Promise.all(urls.map(url => getImage(url, true)));
            images.forEach((image, index) => {
                this.svg = this.svg.replace(urls[index], image);
            })
        }
    }

    static svgToPng(width, height, svg) {
        return new Promise((resolve, reject) => {

            const canvas = createCanvas(width, height);
            canvg(canvas, svg, {
                useCORS: true,
                renderCallback: () => {
                    resolve(canvas);
                }
            });
        })
    }
}
